import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Camera, RefreshCw, RotateCcw, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

const MAX_SIZE = 1024;

/** Toma una foto con la cámara del dispositivo y la devuelve como JPEG base64. */
export const CameraCaptureDialog = ({ open, onOpenChange, onCapture, title }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [snapshot, setSnapshot] = useState(null);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setReady(false);
  }, []);

  const start = useCallback(
    async (mode) => {
      stop();
      setError(null);
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(t("visitors.camera.unsupported"));
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: mode }, width: { ideal: 1280 }, height: { ideal: 960 } },
          audio: false,
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (e) {
        console.error("Error accediendo a la cámara:", e);
        setError(e?.name === "NotAllowedError" ? t("visitors.camera.denied") : t("visitors.camera.error"));
      }
    },
    [stop, t]
  );

  useEffect(() => {
    if (open && !snapshot) start(facingMode);
    if (!open) {
      stop();
      setSnapshot(null);
    }
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facingMode]);

  const capture = () => {
    const video = videoRef.current;
    if (!video?.videoWidth) return;
    const scale = Math.min(1, MAX_SIZE / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    setSnapshot(canvas.toDataURL("image/jpeg", 0.85));
    stop();
  };

  const retake = () => {
    setSnapshot(null);
    start(facingMode);
  };

  const accept = () => {
    onCapture(snapshot);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title || t("visitors.common.takePhoto")}</DialogTitle>
        </DialogHeader>
        <div className="relative w-full aspect-[4/3] bg-black rounded-lg overflow-hidden flex items-center justify-center">
          <video ref={videoRef} playsInline muted className={`w-full h-full object-contain ${snapshot ? "hidden" : ""}`} />
          {snapshot && <img src={snapshot} alt="" className="w-full h-full object-contain" />}
          {error && <p className="absolute inset-x-4 text-center text-sm text-white">{error}</p>}
          {!error && !ready && !snapshot && (
            <div className="absolute w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {snapshot ? (
            <>
              <Button variant="outline" onClick={retake}>
                <RotateCcw className="h-4 w-4 mr-2" />
                {t("visitors.camera.retake")}
              </Button>
              <Button onClick={accept}>
                <Check className="h-4 w-4 mr-2" />
                {t("visitors.camera.usePhoto")}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setFacingMode((m) => (m === "environment" ? "user" : "environment"))}
                disabled={!!error && !navigator.mediaDevices?.getUserMedia}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {t("visitors.camera.switch")}
              </Button>
              <Button onClick={capture} disabled={!ready}>
                <Camera className="h-4 w-4 mr-2" />
                {t("visitors.camera.capture")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
